import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@vision/ui/hooks';
import { updateRuleAction } from '@vision/ui/store/states/survey-logic-builder-state';
import {
  SurveyLogicAction,
  SurveyLogicElement,
  SurveyLogicScope,
  SurveyLogicActionType,
  SurveyLogicTargetOptionKey,
  SurveyLogicTargetStatementKey,
  SURVEY_LOGIC_AVAILABLE_EVENTS,
} from '@vision/ui/interfaces';
import {
  getLabel,
  buildElementOptions,
  buildElementOptionsWithOptions,
  resolveOptionKeyForElement,
} from '@vision/ui/utils';
import { QuestionStyle } from '@vision/ui/enums';

interface UseActionBuilderProps {
  ruleId: string;
  action: SurveyLogicAction;
  elements: SurveyLogicElement[];
  activeLanguageCode: string;
}

export function useActionBuilder({ ruleId, action, elements, activeLanguageCode }: UseActionBuilderProps) {
  const { t } = useTranslation('page-survey-logic-builder');
  const dispatch = useAppDispatch();

  const elementOptions = useMemo(() => {
    return buildElementOptions(elements, activeLanguageCode);
  }, [elements, activeLanguageCode]);

  const elementOptionsWithOptions = useMemo(() => {
    return buildElementOptionsWithOptions(elements, activeLanguageCode);
  }, [elements, activeLanguageCode]);

  const selectedMatrixElements = useMemo(() => {
    if (!Array.isArray(action.targetElementKeys) || !Array.isArray(elements)) return [];

    return elements.filter(
      (el) =>
        action.targetElementKeys.includes(el.key) &&
        el.detail?.style === QuestionStyle.MATRIX &&
        Array.isArray(el.detail?.statements) &&
        el.detail.statements.length > 0,
    );
  }, [action.targetElementKeys, elements]);

  const statementOptions = useMemo(() => {
    if (!Array.isArray(selectedMatrixElements) || selectedMatrixElements.length === 0) return [];

    const options: { value: string; label: string }[] = [];
    const seenValues = new Set<string>();

    selectedMatrixElements.forEach((el) => {
      if (!el?.detail?.statements || !Array.isArray(el.detail.statements)) return;

      el.detail.statements.forEach((stmt: { key: string; body?: Record<string, string> }) => {
        if (!stmt?.key) return;
        const value = `${el.key}:${stmt.key}`;
        if (seenValues.has(value)) return;
        seenValues.add(value);
        options.push({
          value,
          label: getLabel(stmt.body, activeLanguageCode, stmt.key),
        });
      });
    });

    return options;
  }, [selectedMatrixElements, activeLanguageCode]);

  const optionOptions = useMemo(() => {
    if (!Array.isArray(elements)) return [];

    let selectedQuestionKey = action.targetElementKeys?.[0];

    if (!selectedQuestionKey && action.targetOptionKeys?.length) {
      selectedQuestionKey = action.targetOptionKeys[0]?.questionKey;
    }

    if (!selectedQuestionKey) return [];

    const selectedElement = elements.find((el) => el.key === selectedQuestionKey);
    if (!selectedElement?.detail) return [];
    if (!Array.isArray(selectedElement.detail.options)) return [];

    return selectedElement.detail.options
      .filter((opt) => opt?.key)
      .map((opt) => ({
        value: `${selectedQuestionKey}:${opt.key}`,
        label: getLabel(opt.body, activeLanguageCode, opt.key),
      }));
  }, [elements, action.targetElementKeys, action.targetOptionKeys, activeLanguageCode]);

  const eventOptions = useMemo(() => {
    return SURVEY_LOGIC_AVAILABLE_EVENTS.map((event) => ({
      value: event,
      label: t(`event.${event}`),
    }));
  }, [t]);

  const effectiveTargetElementKeys = useMemo(() => {
    if (action.targetElementKeys?.length) {
      return action.targetElementKeys;
    }

    if (action.scope === SurveyLogicScope.OPTION && action.targetOptionKeys?.length) {
      const questionKeys = [...new Set(action.targetOptionKeys.map((tok) => tok.questionKey).filter(Boolean))];
      return questionKeys;
    }

    return [];
  }, [action.targetElementKeys, action.targetOptionKeys, action.scope]);

  const targetOptionValues = useMemo((): string[] => {
    if (!Array.isArray(action.targetOptionKeys)) return [];

    return action.targetOptionKeys
      .filter((tok): tok is SurveyLogicTargetOptionKey => tok != null && typeof tok === 'object')
      .map((tok) => {
        const questionKey = tok.questionKey || '';
        const optionKeyOrLabel = tok.optionKey || '';

        const resolvedOptionKey = resolveOptionKeyForElement(
          elements,
          questionKey,
          activeLanguageCode,
          optionKeyOrLabel,
        );

        if (resolvedOptionKey) {
          return `${questionKey}:${resolvedOptionKey}`;
        }
        return `${questionKey}:${optionKeyOrLabel}`;
      })
      .filter((v) => v !== ':');
  }, [action.targetOptionKeys, elements, activeLanguageCode]);

  const targetStatementValues = useMemo((): string[] => {
    if (!Array.isArray(action.targetStatementKeys)) return [];

    return action.targetStatementKeys
      .filter((tsk): tsk is SurveyLogicTargetStatementKey => tsk != null && typeof tsk === 'object')
      .map((tsk) => {
        const questionKey = tsk.questionKey || '';
        const statementKey = tsk.statementKey || '';
        return `${questionKey}:${statementKey}`;
      })
      .filter((v) => v !== ':');
  }, [action.targetStatementKeys]);

  const handleScopeChange = useCallback(
    (scope: SurveyLogicScope) => {
      dispatch(
        updateRuleAction({
          ruleId,
          ruleAction: {
            ...action,
            scope,
            targetElementKeys: [],
            targetOptionKeys: [],
            targetStatementKeys: [],
            targetEvents: [],
          },
        }),
      );
    },
    [dispatch, ruleId, action],
  );

  const handleActionTypeChange = useCallback(
    (type: SurveyLogicActionType) => {
      dispatch(updateRuleAction({ ruleId, ruleAction: { ...action, type } }));
    },
    [dispatch, ruleId, action],
  );

  const handleTargetElementsChange = useCallback(
    (values: string[]) => {
      dispatch(
        updateRuleAction({
          ruleId,
          ruleAction: {
            ...action,
            targetElementKeys: values,
            targetOptionKeys: action.scope === SurveyLogicScope.OPTION ? [] : action.targetOptionKeys,
          },
        }),
      );
    },
    [dispatch, ruleId, action],
  );

  const handleTargetOptionsChange = useCallback(
    (values: string[]) => {
      const targetOptionKeys: SurveyLogicTargetOptionKey[] = values.map((v) => {
        const [questionKey, optionKey] = v.split(':');
        return { questionKey, optionKey };
      });
      dispatch(updateRuleAction({ ruleId, ruleAction: { ...action, targetOptionKeys } }));
    },
    [dispatch, ruleId, action],
  );

  const handleTargetStatementsChange = useCallback(
    (values: string[]) => {
      const targetStatementKeys: SurveyLogicTargetStatementKey[] = values.map((v) => {
        const [questionKey, statementKey] = v.split(':');
        return { questionKey, statementKey };
      });
      dispatch(updateRuleAction({ ruleId, ruleAction: { ...action, targetStatementKeys } }));
    },
    [dispatch, ruleId, action],
  );

  const handleTargetEventsChange = useCallback(
    (value: string | null) => {
      dispatch(updateRuleAction({ ruleId, ruleAction: { ...action, targetEvents: value ? [value] : [] } }));
    },
    [dispatch, ruleId, action],
  );

  return {
    t,
    elementOptions,
    elementOptionsWithOptions,
    statementOptions,
    optionOptions,
    eventOptions,
    selectedMatrixElements,
    effectiveTargetElementKeys,
    targetOptionValues,
    targetStatementValues,
    handleScopeChange,
    handleActionTypeChange,
    handleTargetElementsChange,
    handleTargetOptionsChange,
    handleTargetStatementsChange,
    handleTargetEventsChange,
  };
}
